'use client'

import React, { ReactElement, useContext, useState } from 'react'
import { formatHotelUrl } from '@/lib/utils/utils'
import classNames from 'classnames'
import { Currency, LinkTarget } from '@/types/Page'
import { ConfigContext } from '@/components/common/ConfigContext'
import { Domain } from '../../../domain-config/domain-config'
import InfiniteScroll from 'react-infinite-scroll-component'
import LoadingIndicator from '@/components/elements/LoadingIndicator'
import Stars from '@/components/common/stars'
import KMWLabel from '@/components/elements/KMWLabel'
import Skeleton from 'react-loading-skeleton'
import {
  HOTEL_FOCUSED_EVENT,
  HOTEL_UNFOCUSED_FOCUSED_EVENT,
} from '@/components/pages/search-page/SearchPage'
import { LikeHotelButton } from '@/components/elements/hotel_tease_element/LikeHotelButton'
import { NumberOfPreloadedImages } from '@/components/ui/search/image-slider/constants'
import { Quantity, SearchPageSearchResultItem, SearchResultOffer } from '@/lib/frontent-api'
import SearchResultHeader from '@/components/pages/search-page/NumberOfResults'
import ImageSlider, {
  SEARCH_PAGE_SIZES,
  SEARCH_PAGE_SIZES_MAP_SEARCH_RESULT,
} from '@/components/ui/search/image-slider/ImageSlider'
import { TopDisruptor } from '@/components/elements/hotel_tease_element/TopDisruptor'
import { TopRankingWrapper } from '@/components/ui/search/TopRankingWrapper'
import { HotelName } from '@/components/ui/search/HotelName'
import { HotelNameSize } from '@/components/ui/search/HotelNameSize'
import { Destinations } from '@/components/ui/search/Destinations'
import CloseButton from '@/components/elements/CloseButton'
import { isWhitelabel } from '@/lib/utils/isWhitelabel'
import Button from '@/components/common/button/Button'
import { ButtonIcon } from '@/components/common/button/constants'
import { ButtonType } from '@/components/common/button/ButtonType'
import Article from '@/components/common/article'
import { useTranslations } from 'next-intl'

export interface BackFillSeparator {
  separator: true
}

export type SearchResultListType = (SearchPageSearchResultItem | BackFillSeparator)[]

function openOfferPage(e: React.MouseEvent, url: string): void {
  const hotelUrl = formatHotelUrl(url)
  if (isWhitelabel(location.hostname)) {
    location.href = hotelUrl
  } else {
    window.open(formatHotelUrl(url), LinkTarget.BLANK)
  }
  e.stopPropagation()
}

function Program(props: SearchResultOffer): ReactElement {
  const [open, setOpen] = useState<boolean>()
  const t = useTranslations('SearchPage')
  const tMisc = useTranslations('Misc')

  return (
    <>
      <div
        className={classNames(
          'col-span-2 grid w-full cursor-pointer grid-cols-[1fr,_auto_] gap-y-1.5 border-t-[1px] px-3 py-2 md:grid-cols-[_1fr,_auto_,_auto_,_auto_] md:gap-y-0 md:px-4 lg:py-1',
          {
            'border-b-[0px]': open,
          },
        )}
        onClick={() => setOpen(!open)}
      >
        <div className="col-start-1 col-end-3 row-start-1 min-h-[24px] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xl font-medium text-stone-800 sm:col-end-2 sm:pr-2 md:col-start-1">
          {props.ProgramName}
        </div>

        {props.Price && (
          <div className="col-start-2 col-end-3 row-start-1 mx-4 hidden items-center self-center md:row-end-3 lg:flex">
            {props.PriceOriginal && (
              <div className="mr-3 inline text-sm text-red-600 line-through lg:mr-0 xl:mr-3">
                {props.PriceOriginal}
              </div>
            )}
            <div className="text-lg font-semibold text-stone-800 lg:hidden xl:block">
              {props.Price}
            </div>
          </div>
        )}
        <div className="col-start-2 col-end-3 row-start-2 row-end-3 flex gap-x-2 self-center sm:row-start-1 md:col-start-3 md:justify-end">
          <Button
            icon={ButtonIcon.ARROW_DOWN_DARK_SEARCH_RESULT_WITHOUT_TEXT_ON_MOBILE}
            smaller={true}
            buttonType={ButtonType.SECONDARY}
            className={classNames(
              'button-flex-fix min-w:[112px] rotate-0 will-change-transform before:translate-y-0 before:duration-200 lg:pr-10',
              {
                'border-[#949292] before:-translate-y-0.5 before:rotate-180': open,
                'before:translate-y-0 before:rotate-0': !open,
              },
            )}
          >
            <span className="hidden lg:block">
              {/*<TranslatedClient id="__SEARCH_PAGE_DESTINATION_PROGRAM_DETAILS_BUTTON_LABEL__" />*/}
              {t('destinationProgramDetailsButtonLabel')}
            </span>
          </Button>
          <Button
            icon={ButtonIcon.ARROW_RIGHT}
            className="hidden text-white xl:inline-flex"
            onClick={(e) => openOfferPage(e, props.Url)}
            smaller={true}
          >
            {tMisc('hotelTeaserOfferButton')}
          </Button>
          <button
            className="block min-w-[90px] rounded-md bg-primary px-4 py-0 text-xl text-white xl:hidden"
            onClick={(e) => openOfferPage(e, props.Url)}
          >
            {props.Price}
          </button>
        </div>

        <div className="col-start-1 col-end-2 row-start-2 min-h-[24px] text-sm text-stone-800 md:col-start-1 md:col-end-2 md:text-xs">
          {t('programTitlePrefix')} {props.ProgramFeatures}
        </div>
      </div>
      <div
        className={classNames('h-0 overflow-hidden px-3 md:px-4', {
          'h-auto border-b-[1px] bg-white pb-4 text-sm md:text-md': open,
        })}
      >
        <Article text={props.ProgramText} />
      </div>
    </>
  )
}

interface HotelSearchResultElementProps {
  searchResultItems: (SearchPageSearchResultItem | BackFillSeparator)[]
  currency: Currency
  loadMore: () => void
  moreHotelSearchResultsAvailable: boolean
  message: string
  backFillSearchResultQuantity: Quantity
  bookmarksPage: boolean
}

export interface SearchResultElementProps {
  withinModal?: boolean
  skeleton?: boolean
  hotel: SearchPageSearchResultItem
  domain: Domain
  hotelName: string
  withoutBottomMargin?: boolean
  closeModal?: () => void
  topBar?: string
  topBarsWithinSameRow?: string
}

interface RatingProps {
  skeleton: boolean
  ratingText: string
  ratingValue: number
  small?: boolean
  additionalClassNames?: string
}

export function Rating(props: RatingProps): ReactElement {
  return (
    <div
      className={classNames(
        'leading-tight',
        {
          'text-sm': !props.small,
          'text-xs': props.small,
        },
        props.additionalClassNames,
      )}
    >
      <div
        className={classNames('mb-0.5 font-medium text-stone-700', {
          'w-[50px]': props.skeleton,
        })}
      >
        {props.skeleton ? <Skeleton /> : props.ratingText}
      </div>

      {!props.skeleton && props.ratingValue ? <Stars rating={props.ratingValue} /> : ''}
      {props.skeleton && (
        <div className="w-20">
          <Skeleton />
        </div>
      )}
    </div>
  )
}

function Description(props: {
  hotel: SearchPageSearchResultItem
  skeleton: boolean
  withinModal: boolean
}): ReactElement {
  const t = useTranslations('SearchPage')

  function displayPopularities(): boolean {
    return !!props.hotel.Popularities && props.hotel.Popularities.length > 0
  }
  const likesCount = t.rich('xTimesTagged', {
    likesCount: () => (
      <span className="color-pr text-secondary-text-color rounded bg-primary-lightened p-0.5">
        {props.hotel.LikesCount}x
      </span>
    ),
  })
  const likesCountElement =
    !props.skeleton && props.hotel.LikesCount > 10 ? (
      <p className="col-start-1 row-span-1 row-start-1 mb-4 hidden justify-self-start text-right align-top text-sm sm:col-start-2 sm:mb-0 lg:inline">
        {likesCount}
      </p>
    ) : (
      <></>
    )

  return (
    <div
      className={classNames('col-span-2 row-span-1 mb-1.5 grid grid-cols-[1fr,auto] gap-x-4', {
        'lg:grid-rows-[auto,1fr]':
          displayPopularities() ||
          (!props.hotel.SpecialCategory && props.hotel.RatingText && !props.skeleton),
        'lg:grid-rows-[50px,38px,38px]': props.hotel.SpecialCategory && !props.skeleton,
        'lg:grid-rows-[30px,auto]':
          !props.hotel.SpecialCategory &&
          !props.hotel.RatingText &&
          !props.skeleton &&
          !displayPopularities(),
        'px-3': props.withinModal,
      })}
    >
      {props.hotel.RatingText && (
        <div className="col-start-1 flex justify-between">
          {props.hotel.RatingText && (
            <Rating
              skeleton={props.skeleton}
              ratingValue={props.hotel.RatingValue}
              ratingText={props.hotel.RatingText}
              additionalClassNames="mb-2"
            />
          )}
        </div>
      )}

      {displayPopularities() && (
        <div
          className={classNames(
            'row-start-2 mb-1 flex min-w-0 flex-wrap items-center overflow-hidden sm:row-start-1',
          )}
        >
          {props.hotel.Popularities &&
            props.hotel.Popularities.map((p) => (
              <TopDisruptor
                displayedKey={p[0]}
                key={p[0] + p[1]}
                value={p[1]}
                additionalClassName="inline-block text-xs [&:not(:last-child)]:mr-2 mb-2"
              />
            ))}
        </div>
      )}
      <div className="col-start-1 text-sm">
        {props.skeleton ? (
          <Skeleton count={3} />
        ) : props.hotel.UniqueSellingPoints.length > 0 ? (
          // Saved to document markup for icon at beginning of line
          props.hotel.UniqueSellingPoints.map((point) => (
            <div
              key={point}
              className="col-span-1 block text-sm before:mr-1.5 before:inline-block before:h-[16px] before:w-[16px] before:translate-y-[6px] before:bg-contain before:bg-no-repeat before:content-[''] before:icon-teaser-characteristic-check md:before:translate-y-[3px] lg:before:bg-center"
            >
              {point}
            </div>
          ))
        ) : (
          <div
            className="col-span-1 line-clamp-5 text-sm lg:line-clamp-none lg:block"
            dangerouslySetInnerHTML={{ __html: props.hotel.Description }}
          />
        )}
      </div>
      {props.hotel.SpecialCategory && (
        <div className="mb-2 inline-flex text-sm">
          <div className="flex h-6 items-center rounded-md border-2 border-primary-lightened">
            <div className="leading-0 relative inline-block overflow-hidden whitespace-nowrap bg-primary-lightened pl-2.5 pr-5 text-stone-800 sm:pr-3.5 custom-breakpoint-1280:pr-5">
              {props.hotel.SpecialCategory}
              <div className="absolute -right-5 bottom-0 top-0 w-6 rotate-45 bg-white md:-right-2 md:h-3.5 md:w-3.5 custom-breakpoint-1280:-right-5 custom-breakpoint-1280:h-6 custom-breakpoint-1280:w-6 "></div>
            </div>
            {props.hotel.Annotation && (
              <div className="leading-0 inline-block overflow-hidden text-ellipsis whitespace-nowrap py-0.5 pl-1 pr-2 text-stone-800">
                {props.hotel.Annotation}
              </div>
            )}
          </div>
        </div>
      )}
      {likesCountElement}
    </div>
  )
}

export function SearchResultElement(props: SearchResultElementProps): ReactElement {
  function hotelFocused(hotelCode: string): void {
    window.dispatchEvent(
      new CustomEvent(HOTEL_FOCUSED_EVENT, {
        detail: {
          hotelCode,
        },
      }),
    )
  }

  function hotelUnFocused(): void {
    window.dispatchEvent(new CustomEvent(HOTEL_UNFOCUSED_FOCUSED_EVENT, {}))
  }

  return (
    <TopRankingWrapper topBar={props.topBar} additionalClassName="mb-6">
      <div
        className={classNames(
          'relative flex h-full flex-col overflow-hidden rounded-md bg-white drop-shadow hover:border-primary',
          { 'mb-6': !props.withinModal && !props.topBar },
        )}
        onMouseEnter={() => hotelFocused(props.hotel.HotelCode)}
        onMouseLeave={() => hotelUnFocused()}
      >
        <div className="grid-rows-[auto,_auto] lg:grid lg:grid-cols-[1.5fr_,_2fr] lg:grid-rows-[14rem] xl:grid-cols-[350px,_2fr]">
          <div className="group relative col-span-2 h-56 w-full min-w-0 cursor-pointer lg:col-span-1 custom-breakpoint-800:col-span-1 custom-breakpoint-1024:col-span-1">
            {props.skeleton ? (
              <div className="m-[14px] h-full rounded-xl md:m-5 lg:max-h-44">
                <Skeleton style={{ height: '100%' }} />
              </div>
            ) : (
              <>
                {!props.closeModal && props.hotel.Discount && (
                  <div className="absolute right-0 top-[15px] z-10 rounded-l-lg bg-highlight-red pb-2 pl-2 pr-2 pt-1 text-right text-white">
                    <div className="-mb-1.5 text-xl font-regular">{props.hotel.Discount}</div>
                  </div>
                )}

                <ImageSlider
                  onClick={(e) => openOfferPage(e, props.hotel.Url)}
                  numberOfImagesToPreload={
                    props.withinModal
                      ? NumberOfPreloadedImages.ForLikelyUse
                      : NumberOfPreloadedImages.ForUnlikelyUse
                  }
                  images={props.hotel.Images}
                  pictureSizeDefinition={
                    props.withinModal ? SEARCH_PAGE_SIZES_MAP_SEARCH_RESULT : SEARCH_PAGE_SIZES
                  }
                  href={formatHotelUrl(props.hotel.Url)}
                  alt={props.hotel.HotelName}
                  openInNewWindow={true}
                  height="224px"
                />
                {props.hotel.IsKmwHotel && <KMWLabel />}
                {props.closeModal && (
                  <CloseButton
                    onClick={props.closeModal}
                    additionalClassNames="rounded-full cursor-pointer absolute right-5 top-5 z-10 h-[32px] w-[32px] bg-[rgba(255,255,255,0.8)] flex items-center justify-center"
                    padding="p-0"
                  />
                )}
              </>
            )}
          </div>
          <div
            className="relative col-span-2 grid grid-rows-[auto_auto_1fr] overflow-hidden from-[rgba(255,255,255,0.3)] to-white
         px-3.5 pt-2 md:px-5
         lg:col-span-1 lg:before:absolute lg:before:bottom-0
         lg:before:h-6 lg:before:w-full lg:before:bg-gradient-to-b lg:before:content-['']
         custom-breakpoint-800:col-span-1"
          >
            <div className="col-span-2 grid grid-cols-[1fr_,_auto] lg:grid-rows-[auto_,_1fr_]">
              <HotelName
                tag={props.skeleton ? 'span' : 'a'}
                domain={props.domain}
                skeleton={!!props.skeleton}
                hotelName={props.hotelName || ''}
                url={props.skeleton ? '' : formatHotelUrl(props.hotel.Url)}
                stars={props.hotel.HotelCategoryStars}
                categoryText={props.hotel.HotelCategoryText}
                size={HotelNameSize.STANDARD}
                additionalClassName="table-cell align-middle"
                openInNewWindow={true}
              />
              {!props.skeleton && (
                <LikeHotelButton
                  hotelCode={props.hotel.HotelCode}
                  additionalClassName="justify-self-end flex items-start justify-end"
                  colorUnselected="#F9F9F9"
                  strokeUnselected="#BDBDBD"
                  strokeSelected="77B800"
                />
              )}
            </div>
            <div className="col-span-2 mb-3 flex justify-between border-b-[1px]">
              <Destinations
                destinations={props.hotel.Destinations}
                classNamesItems="grow leading-tight mb-3"
              />
            </div>
            {!props.withinModal && (
              <Description
                hotel={props.hotel}
                skeleton={!!props.skeleton}
                withinModal={!!props.withinModal}
              />
            )}
          </div>
        </div>
        <div
          className={classNames({
            'flex-grow basis-auto overflow-y-scroll': props.withinModal,
          })}
        >
          {props.withinModal && (
            <Description
              hotel={props.hotel}
              skeleton={!!props.skeleton}
              withinModal={props.withinModal}
            />
          )}
          {!props.skeleton &&
            props.hotel.SearchResultOffers.map((offer) => (
              <Program {...offer} key={offer.ProgramCode} />
            ))}
        </div>
      </div>
    </TopRankingWrapper>
  )
}

export default function SearchResults(props: HotelSearchResultElementProps): ReactElement {
  const domain = useContext(ConfigContext)!.domain
  const translate = useTranslations('SearchPage')

  return (
    <>
      {props.message && (
        <div className="mb-3.5 flex items-center rounded bg-highlight-yellow p-2 text-sm drop-shadow">
          <div className="ml-1 mr-3 h-[30px] w-[30px] min-w-[30px] icon-information-circle" />
          <div className="flex-grow" dangerouslySetInnerHTML={{ __html: props.message }} />
        </div>
      )}

      <InfiniteScroll
        dataLength={props.searchResultItems.length}
        next={props.loadMore}
        hasMore={props.moreHotelSearchResultsAvailable}
        loader={
          <LoadingIndicator additionalClassName="relative left-[calc(50%_-_40px)] -top-[5px]" />
        }
        className="relative"
      >
        {props.searchResultItems.map((hotelTeaserItem, index) => {
          if ('separator' in hotelTeaserItem) {
            return (
              <SearchResultHeader
                key="separator"
                bookmarksPage={false}
                message={translate('resultMessageBackfill', {
                  quantity: props.backFillSearchResultQuantity.NumberOfOffers,
                })}
              />
            )
          } else {
            return (
              <SearchResultElement
                data-key={hotelTeaserItem.HotelCode}
                key={hotelTeaserItem.HotelCode}
                hotel={hotelTeaserItem}
                domain={domain}
                hotelName={hotelTeaserItem.HotelName}
                topBar={index < 3 ? hotelTeaserItem.TopBar : undefined}
              />
            )
          }
        })}
      </InfiniteScroll>
    </>
  )
}
